import React, { useCallback, useEffect, useState, useMemo, useRef } from "react"
import axios, { AxiosError } from "axios"
import clsx from "clsx"
import { CONST_API_DASHBOARD_KPI_ENDPOINT } from "../../../../../endpoints"
import { useKpiForm } from "../KpiContext"
import ChartRentabilite from "../../../../../components/kpi/ChartRentabilite"
import ChartEvolutionsEffectifs from "../../../../../components/kpi/evolutionEffectifs/ChartEvolutionsEffectifs"
import ChartIntercontrat from "../../../../../components/kpi/ChartIntercontrat"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"

interface TabItem {
     id: string
     label: string
     icon: string
}

const KpiCharts = () => {
     const { appliedValues, isSubmitted, error, setError } = useKpiForm()

     // Référence pour le défilement automatique lors de la soumission
     const chartsContainerRef = useRef<HTMLDivElement>(null)

     const [chartData, setChartData] = useState<{
          turnOverCoutMbp: any
          evolutionDesEffectifs: any | null
          intercontrat: any[] | null
     }>({
          turnOverCoutMbp: null,
          evolutionDesEffectifs: null,
          intercontrat: null,
     })

     const [loading, setLoading] = useState<boolean>(false)
     const [activeTab, setActiveTab] = useState<string>("RENTABILITE")

     const fetchKpi = useCallback(async () => {
          if (!appliedValues) return

          setLoading(true)
          setError(null)

          try {
               const response = await axios.post(CONST_API_DASHBOARD_KPI_ENDPOINT, {
                    dateDebut: appliedValues.dateDebut,
                    dateFin: appliedValues.dateFin,
                    personnels: appliedValues.responsables,
                    indicators: appliedValues.indicators,
               })
               setChartData(response.data)

               // Sélection automatique du premier onglet disponible
               if (response.data.turnOverCoutMbp) setActiveTab("RENTABILITE")
               else if (response.data.evolutionDesEffectifs) setActiveTab("EFFECTIFS")
               else if (response.data.intercontrat) setActiveTab("INTERCONTRATS")
          } catch (err: any) {
               console.error("Erreur KPI Global:", err)
               setError("Une erreur est survenue lors de la récupération des données globales.")
          } finally {
               setLoading(false)
          }
     }, [appliedValues, setError])

     useEffect(() => {
          if (isSubmitted && appliedValues) {
               fetchKpi()

               // Scroll vers la section des graphiques après un court délai
               setTimeout(() => {
                    chartsContainerRef.current?.scrollIntoView({
                         behavior: "smooth",
                         block: "start",
                    })
               }, 100)
          }
     }, [appliedValues, isSubmitted, fetchKpi])

     // Génération dynamique des onglets selon les données reçues
     const availableTabs = useMemo(() => {
          const tabs: TabItem[] = []
          if (chartData.turnOverCoutMbp) tabs.push({ id: "RENTABILITE", label: "Rentabilité", icon: "fa-chart-pie" })
          if (chartData.evolutionDesEffectifs) tabs.push({ id: "EFFECTIFS", label: "Effectifs", icon: "fa-users" })
          if (chartData.intercontrat) tabs.push({ id: "INTERCONTRATS", label: "Intercontrats", icon: "fa-clock" })
          return tabs
     }, [chartData])

     if (!isSubmitted) return null

     return (
          <div className="d-flex flex-column gap-5 mb-10" ref={chartsContainerRef}>
               {loading ? (
                    <div className="card shadow-sm p-10 text-center border-0 h-450px d-flex align-items-center justify-content-center">
                         <div className="d-flex flex-column align-items-center">
                              <MySimpleSpinner size="lg" />
                              <span className="text-muted fw-bold mt-3">Analyse des indicateurs en cours...</span>
                         </div>
                    </div>
               ) : error ? (
                    <div className="card shadow-sm p-10 text-center border-danger border-dashed h-450px d-flex align-items-center justify-content-center">
                         <div className="d-flex flex-column align-items-center">
                              <i className="fas fa-exclamation-triangle text-danger fs-1 mb-5"></i>
                              <h3 className="text-gray-800 fw-bolder mb-2">Erreur de chargement</h3>
                              <p className="text-muted fw-bold mb-5">{error}</p>
                              <button className="btn btn-primary" onClick={() => fetchKpi()}>
                                   <i className="fas fa-sync me-2"></i> Réessayer
                              </button>
                         </div>
                    </div>
               ) : (
                    <>
                         {/* NAVIGATION PAR ONGLET (UNIFIÉE MOBILE & DESKTOP) */}
                         {availableTabs.length > 1 && (
                              <div className="d-flex justify-content-center mb-2">
                                   <div className="nav-group nav-group-outline bg-light rounded p-1 d-inline-flex">
                                        {availableTabs.map(tab => (
                                             <button
                                                  key={tab.id}
                                                  type="button"
                                                  onClick={() => setActiveTab(tab.id)}
                                                  className={clsx(
                                                       "btn btn-sm px-6 py-3 fw-bold transition-all d-flex align-items-center gap-2 border-0",
                                                       activeTab === tab.id ? "bg-white shadow-sm text-primary active" : "text-gray-500 text-hover-primary"
                                                  )}
                                             >
                                                  <i className={clsx("fas fs-7", tab.icon, activeTab === tab.id ? "text-primary" : "text-gray-400")}></i>
                                                  {tab.label}
                                             </button>
                                        ))}
                                   </div>
                              </div>
                         )}

                         {/* ZONE DE RENDU DES GRAPHIQUES */}
                         <div className="animate__animated animate__fadeIn animate__faster">
                              {activeTab === "RENTABILITE" && chartData.turnOverCoutMbp && (
                                   <div className="card card-flush shadow-sm p-8 border-0 min-h-500px">
                                        <ChartRentabilite
                                             initialData={chartData.turnOverCoutMbp}
                                             dateDebut={appliedValues?.dateDebut}
                                             dateFin={appliedValues?.dateFin}
                                             personnels={appliedValues?.responsables}
                                        />
                                   </div>
                              )}

                              {activeTab === "EFFECTIFS" && chartData.evolutionDesEffectifs && (
                                   <div className="card card-flush shadow-sm p-8 border-0 min-h-500px">
                                        <ChartEvolutionsEffectifs initialData={chartData.evolutionDesEffectifs} />
                                   </div>
                              )}

                              {activeTab === "INTERCONTRATS" && chartData.intercontrat && (
                                   <div className="card card-flush shadow-sm p-8 border-0 min-h-500px">
                                        <ChartIntercontrat initialData={chartData.intercontrat} />
                                   </div>
                              )}
                         </div>
                    </>
               )}
          </div>
     )
}

export default KpiCharts
